import { FormGroup } from "@cofano/cck";
import Grid from "@mui/material/Grid2";
import { intl } from "LocaleWrapper";
import { ReferenceAdjustmentType, ReferenceRateBase, ReferenceRateUnit, PayableOver } from "__generated__/graphql";
import { LocationLink } from "utils/LinkUtils";
import { DataFormat } from "utils";
import { Fragment, ReactNode } from "react";
import { CalculationMethodDetailsByTypeProps } from "@schemas/calculationmethods/schema";

type DetailsField = {
	id: string;
	render: () => ReactNode;
}

const ReferenceRateCalculationMethodDetails = ({ calculationMethod, currency, session, hiddenFields }: CalculationMethodDetailsByTypeProps) => {

	const ReferenceRateCalculationMethodDetailsFields: DetailsField[] = [
		{
			id: "calculationMethod.route",
			render: () => calculationMethod.referenceRateBase === ReferenceRateBase.Route && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "REFERENCE_RATE_ROUTE" })} overflow="hidden">
						{calculationMethod.route}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.loadLocation",
			render: () => calculationMethod.referenceRateBase === ReferenceRateBase.LoadLocation && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "REFERENCE_RATE_LOAD_LOCATION" })} overflow="hidden">
						<LocationLink location={calculationMethod.loadLocation} />
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.dischargeLocation",
			render: () => calculationMethod.referenceRateBase === ReferenceRateBase.DischargeLocation && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "REFERENCE_RATE_DISCHARGE_LOCATION" })} overflow="hidden">
						<LocationLink location={calculationMethod.dischargeLocation} />
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.pjkTariff",
			render: () => calculationMethod.referenceRateUnit === ReferenceRateUnit.Pjk && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "PJK_TARIFF" })} overflow="hidden">
						{calculationMethod.pjkTariff ? intl.formatMessage({ id: `PJK_TARIFF_${calculationMethod.pjkTariff}` }) : "-"}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.adjustmentAmount",
			render: () => calculationMethod.adjustmentAmount && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "REFERENCE_RATE_ADJUSTMENT_AMOUNT" })} overflow="hidden">
						{calculationMethod.adjustmentType === ReferenceAdjustmentType.Flatprice
							? DataFormat.price(calculationMethod.adjustmentAmount, currency.isoCode, session.locale)
							: DataFormat.percentage(calculationMethod.adjustmentAmount, 2)}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.payableOver",
			render: () =>  (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "PAYABLE_OVER" })} overflow="hidden">
						{intl.formatMessage({ id: `PAYABLE_OVER_${calculationMethod.payableOver}` })}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.preAgreed",
			render: () => calculationMethod.payableOver === PayableOver.Preagreed && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "ORDER_QUANTITY" })} overflow="hidden">
						{DataFormat.tonnage(calculationMethod.preAgreed)}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.referenceRateDateType",
			render: () => (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "REFERENCE_RATE_DATE_TYPE" })} overflow="hidden">
						{calculationMethod.referenceRateDateType ? intl.formatMessage({ id: `REFERENCE_RATE_DATE_TYPE_${calculationMethod.referenceRateDateType}` }) : "-"}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.minimumPrice",
			render: () => (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "MINIMUM_PRICE" })} overflow="hidden">
						{calculationMethod.minimumPrice ? DataFormat.price(calculationMethod.minimumPrice, currency.isoCode, session.locale) : "-"}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.minimumQuantityPayable",
			render: () => (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "MINIMUM_QUANTITY_PAYABLE" })} overflow="hidden">
						{calculationMethod.minimumQuantityPayable ? DataFormat.tonnage(calculationMethod.minimumQuantityPayable) : "-"}
					</FormGroup>
				</Grid>
			),
		},
	];

	return (
		<>
			{ReferenceRateCalculationMethodDetailsFields
				.filter(x => !hiddenFields?.includes(x.id))
				.map(x => (
					<Fragment key={x.id}>
						{x.render()}
					</Fragment>
				))
			}
		</>
	);
};

export default ReferenceRateCalculationMethodDetails;
